import {
  ClickEventComponentNames,
  getUniversalAttributes,
  logEvent,
  logRBIEvent,
} from 'features/analytics';
import { AllowedEvent } from 'features/analytics/allowed-events';
import { CustomEventNames, EventTypes } from 'features/analytics/constants';
import {
  IAmplitudeProduct,
  IAmplitudeProductDetails,
  IAmplitudeSublevelItem,
  ProductItemType,
  Products,
} from 'features/analytics/types';
import { booleanToString } from 'features/analytics/utils';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { compact } from 'lodash-es';

import { IBackendCartEntries, IBaseItem, ILegacyCartEntry, IProductHierarchy } from '@rbi-ctg/menu';
import {
  CartEntryType as GraphQLCartEntryType,
  ICartEntryFragment,
} from 'generated/graphql-gateway';
import { getStaticPathname } from 'navigation/location/location';
import { BranchEventNames } from 'state/branch/branch-event-names';
import { logBranchEvent } from 'state/branch/utils';
import { serializePickupMode } from 'state/service-mode/serializers';
import { ServiceMode } from 'state/service-mode/types';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { isBackendCartEntry } from 'utils/cart-utils';
import { CartEntryType } from 'utils/cart/types';
import { centsToDollars } from 'utils/index';

export type IAddToCartSelectionAttributes = {
  pickerAspectSelection?: boolean;
  comboSlotSelection?: boolean;
  itemModified?: boolean;
};

type CartEventOptions = {
  action: 'add' | 'remove';
  cartEntry: ILegacyCartEntry;
  previousCartEntries: ILegacyCartEntry[];
  selectionAttrs?: IAddToCartSelectionAttributes;
  isReward?: boolean;
  deviceTime: string;
  sourcePage: string;
  pickupMode: string;
};

export const buildCartMutationEvents = ({
  action,
  cartEntry,
  previousCartEntries,
  selectionAttrs,
  isReward = false,
  deviceTime,
  sourcePage,
  pickupMode,
}: CartEventOptions) => {
  const product = createProduct(cartEntry);
  if (!product) {
    return null;
  }

  const cartId = isBackendCartEntry(cartEntry) ? cartEntry.lineId : cartEntry.cartId;
  const attributes = {
    isDonation: cartEntry.isDonation ?? false,
    isExtra: cartEntry.isExtra ?? false,
    cartId: cartId || cartEntry._id,
    'Is Kiosk': booleanToString(false),
    'Device Time': deviceTime,
    'Source Page': sourcePage,
    'Is Update': booleanToString(false),
    'Cart Data': getCartDataItems(previousCartEntries),
    'Picker Aspect Selection': booleanToString(!!selectionAttrs?.pickerAspectSelection),
    'Combo Slot Selection': booleanToString(!!selectionAttrs?.comboSlotSelection),
    'Item Modified': booleanToString(!!selectionAttrs?.itemModified),
    'Total Amount': product.total_product_amount,
    'Pickup Mode': pickupMode,
  };

  const itemType = isReward ? 'reward' : cartEntry.type.includes('Offer') ? 'offer' : 'product';
  const commerceAttributes =
    action === 'add'
      ? {
          wasModified: !!(
            selectionAttrs?.comboSlotSelection || selectionAttrs?.pickerAspectSelection
          ),
          wasCustomized: !!selectionAttrs?.itemModified,
          ...attributes,
          products: [product],
        }
      : { itemType, ...attributes, products: [product] };

  return {
    productDetailsEvent: {
      name:
        action === 'add'
          ? CustomEventNames.E_COMMERCE_ADD_TO_CART
          : CustomEventNames.E_COMMERCE_REMOVE_FROM_CART,
      products: [cartEntry],
      attributes: commerceAttributes,
    } as ILogProductDetailsEvent,
    itemEvent: {
      name: action === 'add' ? 'eCommerce - AddToCart - Item' : 'eCommerce - RemoveFromCart - Item',
      type: EventTypes.Other,
      attributes: { products: [], ...attributes, ...convertToSingleCommerceProduct(product) },
    } as AllowedEvent,
  };
};

type LogCartMutationOptions = Omit<CartEventOptions, 'deviceTime' | 'sourcePage' | 'pickupMode'>;

export const logCartMutation = (options: LogCartMutationOptions) => {
  const events = buildCartMutationEvents({
    ...options,
    deviceTime: new Date().toTimeString().split(' ')[0],
    sourcePage: getStaticPathname(),
    pickupMode: getUniversalAttributes()['Pickup Mode'] ?? '',
  });
  if (!events) {
    return;
  }

  if (options.action === 'add') {
    logBranchEvent(BranchEventNames.ADD_TO_CART);
  }

  logProductDetailsEvent(events.productDetailsEvent);
  logRBIEvent(events.itemEvent);
};

export interface ICartEntryLoggable extends ICartEntryFragment {
  children?: ICartEntryLoggable[];
  productHierarchy?: IProductHierarchy | null;
  productNumber?: string;
  cartId?: string;
}

export type ProductDetailsEventName =
  | CustomEventNames.UPSELL_DISPLAYED
  | CustomEventNames.E_COMMERCE_PURCHASE
  | CustomEventNames.E_COMMERCE_ADD_TO_CART
  | CustomEventNames.E_COMMERCE_REMOVE_FROM_CART
  | CustomEventNames.EDIT_CART
  | CustomEventNames.PRODUCT_FAVORITE_BUTTON_CLICKED;

export interface ILogProductDetailsEvent {
  name: ProductDetailsEventName;
  products: ILegacyCartEntry[] | IBaseItem[] | ICartEntryLoggable[];
  price?: number;
  attributes?: Record<string, string | number | boolean | IAmplitudeProduct[] | Products>;
  logOptions?: { skipLoggingToBraze: boolean };
}

export function flattenCartEntryItems(cartEntry: ILegacyCartEntry): ILegacyCartEntry[] {
  const children = cartEntry.children.reduce((accum: ILegacyCartEntry[], current) => {
    return [...accum, ...flattenCartEntryItems(current)];
  }, []);
  return [cartEntry, ...children];
}

const createSublevelItems = (cartEntry: ILegacyCartEntry): IAmplitudeSublevelItem[] => {
  const subItems = flattenCartEntryItems(cartEntry).filter(
    item => item._id !== cartEntry._id && item.type === CartEntryType.item
  );
  const mergedItemsById = subItems.reduce<Record<string, IAmplitudeSublevelItem | undefined>>(
    (acc, item) => {
      const currentItem = acc[item._id];
      if (currentItem) {
        currentItem.quantity += item.quantity;
      } else {
        acc[item._id] = { id: item._id, quantity: item.quantity };
      }
      return acc;
    },
    {}
  );
  return compact(Object.values(mergedItemsById));
};

const createSublevelProducts = (cartEntry: ILegacyCartEntry): IAmplitudeProduct[] => {
  const subProducts: IAmplitudeProduct[] = [];

  for (const subItem of flattenCartEntryItems(cartEntry)) {
    if (
      subItem._id !== cartEntry._id &&
      subItem.type === CartEntryType.item &&
      subItem.productHierarchy
    ) {
      const product = createProduct(subItem);
      if (product) {
        product.custom_attributes.comboChild = booleanToString(true);
        product.comboChild = booleanToString(true);
        subProducts.push(product);
      }
    }
  }
  return subProducts;
};

export const reformatAttributesForSingularProduct = (product: IAmplitudeProduct) => {
  const newAttributes: Record<string, any> = { ...product };
  newAttributes.Name = product.name ?? '';
  newAttributes.Id = product.id ?? '';
  newAttributes.Price = String(product.price ?? '');
  newAttributes.Quantity = String(product.quantity ?? '');
  newAttributes['Total Product Amount'] = String(product.total_product_amount ?? '');
  delete newAttributes.id;
  delete newAttributes.name;
  delete newAttributes.quantity;
  delete newAttributes.price;
  delete newAttributes.total_product_amount;
  return newAttributes;
};

export const createProduct = (
  cartEntry: ILegacyCartEntry | IBackendCartEntries
): IAmplitudeProduct | null => {
  const id = '_id' in cartEntry ? cartEntry._id : cartEntry.sanityId;
  if (!id) {
    return null;
  }
  const cartId = isBackendCartEntry(cartEntry) ? cartEntry.lineId : cartEntry.cartId;
  const { name = '', price, quantity, isDonation = false, isExtra = false } = cartEntry;
  const basePrice = price ? centsToDollars(price / quantity) : 0;
  const sublevelItems = createSublevelItems(cartEntry as ILegacyCartEntry);
  const itemLevel = sublevelItems.length === 0 ? ProductItemType.Child : ProductItemType.Parent;
  const productAttributes = {
    cartId: cartId || id,
    comboChild: booleanToString(false),
    isDonation: booleanToString(isDonation),
    isExtra: booleanToString(isExtra),
    'Item Level': itemLevel,
    sublevelItems: JSON.stringify(sublevelItems),
  };
  const hierarchyData = {
    L1: cartEntry.productHierarchy?.L1 || '',
    L2: cartEntry.productHierarchy?.L2 || '',
    L3: cartEntry.productHierarchy?.L3 || '',
    L4: cartEntry.productHierarchy?.L4 || '',
    L5: cartEntry.productHierarchy?.L5 || '',
  };

  return {
    name,
    id,
    quantity,
    price: basePrice,
    total_product_amount: basePrice * quantity,
    custom_attributes: {
      ...productAttributes,
      ...(itemLevel === ProductItemType.Child && hierarchyData),
    },
    ...(itemLevel === ProductItemType.Child && hierarchyData),
    ...productAttributes,
  };
};

export function createCRMProducts({
  serverOrder,
  cartEntries,
}: {
  serverOrder: import('@rbi-ctg/menu').IServerOrder;
  cartEntries: ILegacyCartEntry[];
}) {
  return cartEntries.reduce<IAmplitudeProduct[]>((products, cartEntry) => {
    const product = createProduct(cartEntry);
    if (!product) {
      return products;
    }
    const rewardApplied = serverOrder.cart.rewardsApplied?.find(
      reward => reward?.cartId === product.custom_attributes?.cartId
    );
    product.rewardItem = booleanToString(!!rewardApplied);
    product.custom_attributes = {
      ...product.custom_attributes,
      rewardItem: booleanToString(!!rewardApplied),
    };
    return [...products, product, ...createSublevelProducts(cartEntry)];
  }, []);
}

export const getCartDataItems = (cartEntries: ILegacyCartEntry[]): string => {
  const cartData = JSON.stringify({
    items: cartEntries.map(entry => ({ items: createProduct(entry) })),
  });
  return cartData.match(/item_\d{3,}/gi)?.join() || '';
};

export function convertToSingleCommerceProduct(product: Partial<IAmplitudeProduct>) {
  const { id, quantity, name, price, total_product_amount, ...rest } = product;
  return {
    ...rest,
    Quantity: String(quantity),
    Id: String(id),
    Name: String(name),
    'Total Product Amount': String(total_product_amount),
    Price: String(price),
  };
}

const isBaseItemArray = (
  items: IBaseItem[] | ILegacyCartEntry[] | ICartEntryLoggable[]
): items is IBaseItem[] => items.length > 0 && '_type' in items[0];

const isCartEntryArray = (
  items: IBaseItem[] | ILegacyCartEntry[] | ICartEntryLoggable[]
): items is ILegacyCartEntry[] | ICartEntryLoggable[] => items.length > 0 && 'type' in items[0];

const convertPriceToNumber = (price: string | number): number =>
  typeof price === 'number' ? price : parseInt(price, 10) / 100;

const validEntriesTypes = [CartEntryType.combo, CartEntryType.item, ''];

const flattenCartEntries = (
  cartEntries: ILegacyCartEntry[] | ICartEntryLoggable[],
  parent?: ILegacyCartEntry | ICartEntryLoggable
): IAmplitudeProductDetails[] =>
  cartEntries.reduce<IAmplitudeProductDetails[]>((products, entry) => {
    if (validEntriesTypes.includes(entry.type)) {
      products.push({
        name: entry.name || '',
        sanityId: entry.sanityId || entry._id,
        quantity: parent ? entry.quantity * parent.quantity : entry.quantity,
        price: entry.price ? convertPriceToNumber(entry.price) : 0,
        productType: entry.type,
        parentName: parent?.name || '',
        parentSanityId: parent?.sanityId || parent?._id,
        bkpn: entry.productNumber,
        foodType: entry.productHierarchy?.L1,
        category: entry.productHierarchy?.L2,
        segment: entry.productHierarchy?.L3,
        platform: entry.productHierarchy?.L4,
        menuItemName: entry.productHierarchy?.L5,
        isExtra: entry.isExtra || '',
      });
    }
    if (entry.children?.length) {
      products.push(...flattenCartEntries(entry.children, entry));
    }
    return products;
  }, []);

export function getProductsDetails(
  products: IBaseItem[] | ILegacyCartEntry[] | ICartEntryLoggable[],
  price = 0
): IAmplitudeProductDetails[] {
  if (isBaseItemArray(products)) {
    return products.map(item => ({
      name: item.name?.locale,
      sanityId: item._id,
      price,
      bkpn: item.operationalItem?.productNumber,
      productType: item._type,
      foodType: item.productHierarchy?.L1,
      category: item.productHierarchy?.L2,
      segment: item.productHierarchy?.L3,
      platform: item.productHierarchy?.L4,
      menuItemName: item.productHierarchy?.L5,
    }));
  }
  return isCartEntryArray(products) ? flattenCartEntries(products) : [];
}

export const logProductDetailsEvent = (
  { name, products, price = 0, attributes = {} }: ILogProductDetailsEvent,
  logOptions?: { flushBrazeAfterLog?: boolean; skipLoggingToBraze?: boolean }
) => {
  logRBIEvent(
    {
      name,
      type: EventTypes.Other,
      attributes: { productsDetails: getProductsDetails(products, price), ...attributes },
    },
    logOptions
  );
};

type ProductTypeHierarchyFields = {
  productType?: string;
  foodType?: string;
  category?: string;
  segment?: string;
  platform?: string;
  menuItemName?: string;
};

export const mapProductTypeHierarchyToAnalyticsFields = (
  productTypeHierarchy?: readonly string[] | null
): ProductTypeHierarchyFields => ({
  productType: productTypeHierarchy?.[0],
  foodType: productTypeHierarchy?.[1],
  category: productTypeHierarchy?.[2],
  segment: productTypeHierarchy?.[3],
  platform: productTypeHierarchy?.[4],
  menuItemName: productTypeHierarchy?.[5],
});

type CartModalName =
  | 'Minimal cart pricing error'
  | 'Delivery minimum subtotal'
  | 'Cart selection required';

type CartModalActionName = 'Okay' | 'Try Again' | 'Try another store';

type CartUpsellDisplayedEventAttributes = IAmplitudeProductDetails;

type CartUpsellAddedEventAttributes = {
  name: string;
  sanityId: string;
  price?: number;
  upsellItemPosition?: number;
};

type AmplitudeSublevelItem = {
  id: string;
  quantity: number;
};

type CartCheckoutAttributes = {
  'Pickup Mode': string;
  'Cart Data': string;
  'Has Upsell': boolean;
  'Has Donation': boolean;
  'Has Offer': boolean;
  'Has Extra': boolean;
  'Total Amount': number;
  'Product Count': number;
};

type CartAddToCartAttributes = {
  isDonation: boolean;
  isExtra: boolean;
  cartId: string;
  'Is Kiosk': string;
  'Device Time': string;
  'Source Page': string;
  'Is Update': string;
  'Cart Data': string;
  'Picker Aspect Selection': string;
  'Combo Slot Selection': string;
  'Item Modified': string;
  'Total Amount': number;
  'Pickup Mode': string;
};

type CartRemoveFromCartAttributes = CartAddToCartAttributes & {
  itemType: 'product' | 'offer' | 'reward';
};

type CartAddToCartSelectionAttributes = {
  pickerAspectSelection?: boolean;
  comboSlotSelection?: boolean;
  itemModified?: boolean;
};

const buttonClickBaseAttributes = {
  component: ClickEventComponentNames.BUTTON,
};

const toDollars = (priceInCents?: number | null) =>
  typeof priceInCents === 'number' ? priceInCents / 100 : undefined;

const isOfferCartEntry = (cartEntry: ICartEntry) =>
  cartEntry.type === MinimalCartEntryType.discount ||
  cartEntry.type === MinimalCartEntryType.incentive;

const toAmplitudeCartEntryType = (cartEntry: ICartEntry): ICartEntryLoggable['type'] => {
  if (cartEntry.type === MinimalCartEntryType.combo) {
    return GraphQLCartEntryType.COMBO;
  }

  if (cartEntry.type === MinimalCartEntryType.item) {
    return GraphQLCartEntryType.ITEM;
  }

  return '' as ICartEntryLoggable['type'];
};

const toLoggableCartEntry = (cartEntry: ICartEntry): ICartEntryLoggable => ({
  _id: cartEntry.id,
  cartId: cartEntry.cartId,
  children: (cartEntry.children ?? []).map(toLoggableCartEntry),
  lineId: cartEntry.cartId ?? cartEntry.id,
  name: cartEntry.name,
  price: toDollars(cartEntry.price?.amount) ?? 0,
  quantity: cartEntry.quantity,
  sanityId: cartEntry.id,
  type: toAmplitudeCartEntryType(cartEntry),
  isExtra: cartEntry.type === MinimalCartEntryType.extra || cartEntry.isExtra,
});

const getUpsellEventAttributes = (
  menuEntry: ILocalizedMenuEntry,
  priceInCents?: number
): CartUpsellDisplayedEventAttributes => {
  const productTypeFields = mapProductTypeHierarchyToAnalyticsFields(menuEntry.productType);

  return {
    name: menuEntry.name?.locale ?? '',
    sanityId: menuEntry.id,
    ...(typeof priceInCents === 'number' ? { price: toDollars(priceInCents) } : {}),
    productType: productTypeFields.productType ?? String(menuEntry.type ?? '').toLowerCase(),
    foodType: productTypeFields.foodType,
    category: productTypeFields.category,
    segment: productTypeFields.segment,
    platform: productTypeFields.platform,
    menuItemName: productTypeFields.menuItemName,
  };
};

const getUpsellAddedEventAttributes = ({
  menuEntry,
  priceInCents,
  upsellItemPosition,
}: {
  menuEntry: ILocalizedMenuEntry;
  priceInCents?: number;
  upsellItemPosition?: number;
}): CartUpsellAddedEventAttributes => ({
  name: menuEntry.name?.locale ?? '',
  sanityId: menuEntry.id,
  ...(typeof priceInCents === 'number' ? { price: toDollars(priceInCents) } : {}),
  ...(typeof upsellItemPosition === 'number' ? { upsellItemPosition } : {}),
});

const logCartButtonClick = (name: string, attributes: Record<string, string> = {}) => {
  logButtonClick({
    attributes: {
      ...buttonClickBaseAttributes,
      name,
      Name: name,
      ...attributes,
    },
  });
};

const getCheckoutProductDescendants = (cartEntry: ICartEntry): ICartEntry[] =>
  (cartEntry.children ?? []).flatMap(child => [child, ...getCheckoutProductDescendants(child)]);

const getCheckoutSublevelItems = (cartEntry: ICartEntry): AmplitudeSublevelItem[] => {
  const subItems = getCheckoutProductDescendants(cartEntry).filter(
    item => item.id !== cartEntry.id && item.type === MinimalCartEntryType.item
  );

  const mergedItemsById = subItems.reduce<Record<string, AmplitudeSublevelItem>>((acc, item) => {
    const currentItem = acc[item.id];

    if (currentItem) {
      currentItem.quantity += item.quantity;
    } else {
      acc[item.id] = { id: item.id, quantity: item.quantity };
    }

    return acc;
  }, {});

  return Object.values(mergedItemsById);
};

const toCheckoutAmplitudeProduct = (cartEntry: ICartEntry): IAmplitudeProduct | null => {
  if (!cartEntry.id) {
    return null;
  }

  const quantity = cartEntry.quantity ?? 1;
  const unitPrice = toDollars(cartEntry.price?.amount) ?? 0;
  const sublevelItems = getCheckoutSublevelItems(cartEntry);
  const itemLevel = sublevelItems.length === 0 ? ProductItemType.Child : ProductItemType.Parent;

  const productAttributes = {
    cartId: cartEntry.cartId || cartEntry.id,
    comboChild: booleanToString(false),
    isDonation: booleanToString(
      cartEntry.type === MinimalCartEntryType.donation || !!cartEntry.isDonation
    ),
    isExtra: booleanToString(cartEntry.type === MinimalCartEntryType.extra || !!cartEntry.isExtra),
    'Item Level': itemLevel,
    sublevelItems: JSON.stringify(sublevelItems),
  };

  return {
    name: cartEntry.name,
    id: cartEntry.id,
    quantity,
    price: unitPrice,
    total_product_amount: unitPrice * quantity,
    custom_attributes: productAttributes,
    ...productAttributes,
  };
};

const getCheckoutProducts = (cartEntries: ICartEntry[]) =>
  cartEntries
    .map(toCheckoutAmplitudeProduct)
    .filter((product): product is IAmplitudeProduct => !!product);

const getCheckoutCartDataItems = (products: IAmplitudeProduct[]) => {
  const cartData = JSON.stringify({
    items: products.map(product => ({
      items: product,
    })),
  });
  const cartDataItemsRegex = /item_\d{3,}/gi;

  return cartData.match(cartDataItemsRegex)?.join() || '';
};

const getCartCheckoutAttributes = ({
  cartEntries,
  products,
  serviceMode,
}: {
  cartEntries: ICartEntry[];
  products: IAmplitudeProduct[];
  serviceMode: ServiceMode;
}): CartCheckoutAttributes => ({
  'Pickup Mode': serializePickupMode(serviceMode),
  'Cart Data': getCheckoutCartDataItems(products),
  'Has Upsell': cartEntries.some(entry => !!entry.isUpsell),
  'Has Donation': cartEntries.some(
    entry => entry.type === MinimalCartEntryType.donation || !!entry.isDonation
  ),
  'Has Offer': cartEntries.some(entry => isOfferCartEntry(entry)),
  'Has Extra': cartEntries.some(
    entry => entry.type === MinimalCartEntryType.extra || !!entry.isExtra
  ),
  'Total Amount': products.reduce((total, product) => total + product.total_product_amount, 0),
  'Product Count': products.length,
});

const getCartAddToCartAttributes = ({
  cartEntry,
  previousCartEntries,
  product,
  serviceMode,
  selectionAttrs,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  product: IAmplitudeProduct;
  serviceMode: ServiceMode;
  selectionAttrs?: CartAddToCartSelectionAttributes;
}): CartAddToCartAttributes => ({
  isDonation: cartEntry.type === MinimalCartEntryType.donation || !!cartEntry.isDonation,
  isExtra: cartEntry.type === MinimalCartEntryType.extra || !!cartEntry.isExtra,
  cartId: cartEntry.cartId || cartEntry.id,
  'Is Kiosk': booleanToString(false),
  'Device Time': getDeviceTime(),
  'Source Page': getStaticPathname(),
  'Is Update': booleanToString(false),
  'Cart Data': getCheckoutCartDataItems(getCheckoutProducts(previousCartEntries)),
  'Picker Aspect Selection': booleanToString(!!selectionAttrs?.pickerAspectSelection),
  'Combo Slot Selection': booleanToString(!!selectionAttrs?.comboSlotSelection),
  'Item Modified': booleanToString(!!selectionAttrs?.itemModified),
  'Total Amount': product.total_product_amount,
  'Pickup Mode': serializePickupMode(serviceMode),
});

const getCartRemoveFromCartAttributes = ({
  cartEntry,
  previousCartEntries,
  product,
  serviceMode,
  isReward = false,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  product: IAmplitudeProduct;
  serviceMode: ServiceMode;
  isReward?: boolean;
}): CartRemoveFromCartAttributes => ({
  ...getCartAddToCartAttributes({
    cartEntry,
    previousCartEntries,
    product,
    serviceMode,
  }),
  itemType: isReward ? 'reward' : isOfferCartEntry(cartEntry) ? 'offer' : 'product',
});

export const CartAnalyticsModalNames = {
  PRICING_ERROR: 'Minimal cart pricing error' as CartModalName,
  DELIVERY_MINIMUM_SUBTOTAL: 'Delivery minimum subtotal' as CartModalName,
  SELECTION_REQUIRED: 'Cart selection required' as CartModalName,
};

export const CartAnalyticsModalActionNames = {
  OKAY: 'Okay' as CartModalActionName,
  TRY_AGAIN: 'Try Again' as CartModalActionName,
  TRY_ANOTHER_STORE: 'Try another store' as CartModalActionName,
};

export const CartAnalyticsCommerceEventNames = {
  ADD_TO_CART: CustomEventNames.E_COMMERCE_ADD_TO_CART as const,
  ADD_TO_CART_ITEM: 'eCommerce - AddToCart - Item' as const,
  CHECKOUT: CustomEventNames.E_COMMERCE_CHECKOUT as const,
  CHECKOUT_ITEM: 'eCommerce - Checkout - Item' as const,
  REMOVE_FROM_CART: CustomEventNames.E_COMMERCE_REMOVE_FROM_CART as const,
  REMOVE_FROM_CART_ITEM: 'eCommerce - RemoveFromCart - Item' as const,
};

const getDeviceTime = () => new Date().toTimeString().split(' ')[0];

export const logCartItemDuplicated = () => {
  logCartButtonClick(CustomEventNames.BUTTON_CLICK_DUPLICATE_ITEM);
};

export const logCartItemRemoved = () => {
  logCartButtonClick(CustomEventNames.BUTTON_CLICK_CART_ITEM_REMOVED);
};

export const logCartItemEdited = ({
  cartEntry,
  editUrl,
}: {
  cartEntry: ICartEntry;
  editUrl: string;
}) => {
  logProductDetailsEvent({
    name: CustomEventNames.EDIT_CART,
    products: [toLoggableCartEntry(cartEntry)],
    attributes: {
      'Product Id': cartEntry.id,
      'Product Name': cartEntry.name,
      'Edit Url': editUrl,
      'Is Reorder': booleanToString(false),
      'Is Offer': booleanToString(isOfferCartEntry(cartEntry)),
    },
  });
};

export const logCartContinuePressed = () => {
  logCartButtonClick(CustomEventNames.BUTTON_CLICK_PLACE_ORDER);
};

export const logCartCheckoutStarted = ({
  cartEntries,
  serviceMode,
}: {
  cartEntries: ICartEntry[];
  serviceMode: ServiceMode;
}) => {
  const products = getCheckoutProducts(cartEntries);

  if (!products.length) {
    return;
  }

  const checkoutAttributes = getCartCheckoutAttributes({
    cartEntries,
    products,
    serviceMode,
  });

  logRBIEvent({
    name: CartAnalyticsCommerceEventNames.CHECKOUT,
    type: EventTypes.Other,
    attributes: {
      ...checkoutAttributes,
      products,
    },
  });

  products.forEach(product => {
    logEvent(CartAnalyticsCommerceEventNames.CHECKOUT_ITEM, EventTypes.Other, {
      ...checkoutAttributes,
      ...convertToSingleCommerceProduct(product),
    });
  });
};

export const logCartAddToCart = ({
  cartEntry,
  previousCartEntries,
  serviceMode,
  selectionAttrs,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  serviceMode: ServiceMode;
  selectionAttrs?: CartAddToCartSelectionAttributes;
}) => {
  const product = toCheckoutAmplitudeProduct(cartEntry);

  if (!product) {
    return;
  }

  const addToCartAttributes = getCartAddToCartAttributes({
    cartEntry,
    previousCartEntries,
    product,
    serviceMode,
    selectionAttrs,
  });

  logProductDetailsEvent({
    name: CartAnalyticsCommerceEventNames.ADD_TO_CART,
    products: [toLoggableCartEntry(cartEntry)],
    attributes: {
      wasModified: !!(selectionAttrs?.comboSlotSelection || selectionAttrs?.pickerAspectSelection),
      wasCustomized: !!selectionAttrs?.itemModified,
      ...addToCartAttributes,
      products: [product],
    },
  });

  logRBIEvent({
    name: CartAnalyticsCommerceEventNames.ADD_TO_CART_ITEM,
    type: EventTypes.Other,
    attributes: {
      products: [],
      ...addToCartAttributes,
      ...convertToSingleCommerceProduct(product),
    },
  });
};

export const logCartRemoveFromCart = ({
  cartEntry,
  previousCartEntries,
  serviceMode,
  isReward = false,
}: {
  cartEntry: ICartEntry;
  previousCartEntries: ICartEntry[];
  serviceMode: ServiceMode;
  isReward?: boolean;
}) => {
  const product = toCheckoutAmplitudeProduct(cartEntry);

  if (!product) {
    return;
  }

  const removeFromCartAttributes = getCartRemoveFromCartAttributes({
    cartEntry,
    previousCartEntries,
    product,
    serviceMode,
    isReward,
  });

  logProductDetailsEvent({
    name: CartAnalyticsCommerceEventNames.REMOVE_FROM_CART,
    products: [toLoggableCartEntry(cartEntry)],
    attributes: {
      ...removeFromCartAttributes,
      products: [product],
    },
  });

  logRBIEvent({
    name: CartAnalyticsCommerceEventNames.REMOVE_FROM_CART_ITEM,
    type: EventTypes.Other,
    attributes: {
      products: [],
      ...removeFromCartAttributes,
      ...convertToSingleCommerceProduct(product),
    },
  });
};

export const logCartDonationViewed = () => {
  logRBIEvent({
    name: CustomEventNames.FOUNDATION_DONATIONS_VIEW,
    type: EventTypes.Other,
    attributes: {},
  });
};

export const logCartUpsellDisplayed = ({
  menuEntry,
  priceInCents,
}: {
  menuEntry: ILocalizedMenuEntry;
  priceInCents?: number;
}) => {
  logRBIEvent({
    name: CustomEventNames.UPSELL_DISPLAYED,
    type: EventTypes.Other,
    attributes: {
      productsDetails: [getUpsellEventAttributes(menuEntry, priceInCents)],
    },
  });
};

export const logCartUpsellAdded = ({
  menuEntry,
  priceInCents,
  upsellItemPosition,
}: {
  menuEntry: ILocalizedMenuEntry;
  priceInCents?: number;
  upsellItemPosition?: number;
}) => {
  logRBIEvent({
    name: CustomEventNames.UPSELL_ADDED,
    type: EventTypes.Other,
    attributes: getUpsellAddedEventAttributes({
      menuEntry,
      priceInCents,
      upsellItemPosition,
    }),
  });
};

export const logCartModalAction = ({
  actionName,
  modalName,
}: {
  actionName: CartModalActionName;
  modalName: CartModalName;
}) => {
  logCartButtonClick(actionName, {
    modalName,
  });
};

export const logCartContinue = () => {
  logButtonClick({
    attributes: {
      component: ClickEventComponentNames.BUTTON,
      name: 'Continue in Cart',
    },
  });
};
export const logCartPlaceOrder = () => {
  logButtonClick({
    attributes: {
      component: ClickEventComponentNames.BUTTON,
      name: 'Place Secure Order',
    },
  });
};
export const logCartContinueOrder = () => {
  logButtonClick({
    attributes: {
      component: ClickEventComponentNames.BUTTON,
      name: 'Continue',
    },
  });
};
